// src/Services/DocMasterService.js
import { ServiceProvider } from "./ServiceProvider";
import apiCall from "../utils/apiInterceptor";

class DocMasterService {
    static async getAll(page_no = 1, count = 10, search = "") {
        try {
            let url = "";

            // ✅ Use filter API if search has value
            if (search && search.trim() !== "") {
                url = `${ServiceProvider.docMaster.filter}?search_text=${encodeURIComponent(search)}&page_no=${page_no}&count=${count}`;
            }
            // ✅ Otherwise, use normal getAll API
            else {
                url = `${ServiceProvider.docMaster.getAll}?page_no=${page_no}&count=${count}`;
            }

            const response = await apiCall(url, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch documents");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching documents:", error);
            throw new Error(error.message || "Unable to process your request");
        }
    }
    
    static async getAllPrint(page_no = 1, count = 10, search = "") {
        try {
            let url = "";

            // ✅ Use filter API if search has value
            if (search && search.trim() !== "") {
                url = `${ServiceProvider.docMaster.filterPrint}?search_text=${encodeURIComponent(search)}&page_no=${page_no}&count=${count}`;
            }
            // ✅ Otherwise, use normal getAll API
            else {
                url = `${ServiceProvider.docMaster.getAll}?page_no=${page_no}&count=${count}`;
            }

            const response = await apiCall(url, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch documents");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching documents:", error);
            throw new Error(error.message || "Unable to process your request");
        }
    }

    static async create(bomData) {
        try {
            const response = await apiCall(ServiceProvider.docMaster.create, {
                method: "POST",
                body: JSON.stringify(bomData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create BOM");
            }

            return await response.json();
        } catch (error) {
            console.error("Error creating BOM:", error);
            throw new Error(error.message || "Failed to create BOM");
        }
    }

    static async update(id, data) {
        try {
            const response = await apiCall(`${ServiceProvider.docMaster.update}/${id}`, {
                method: "POST",
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update Doc Master");
            }

            return await response.json();
        } catch (error) {
            console.error("Error updating Doc Master:", error);
            throw new Error(error.message || "Failed to update Doc Master");
        }
    }

    static async delete(id) {
        try {
            const response = await apiCall(`${ServiceProvider.docMaster.delete}/${id}`, {
                method: "DELETE",
             });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete Doc Master");
            }

            return await response.json();
        } catch (error) {
            console.error("Error deleting Doc Master:", error);
            throw new Error(error.message || "Failed to delete Doc Master");
        }
    }

    static async replaceItems(id, data) {
        try {
            const response = await apiCall(`${ServiceProvider.docMaster.replaceItems}/${id}`, {
                method: "PUT",
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to replace items");
            }

            return await response.json();
        } catch (error) {
            console.error("Error replacing items:", error);
            throw new Error(error.message || "Failed to update Doc Master");
        }
    }

    static async uploadAsset(data) {
        try {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');

            // ⚠️ Note: For FormData, we need to handle this differently
            // apiCall adds Content-Type: application/json by default
            // For file uploads, we need to skip that
            const response = await fetch(ServiceProvider.docMaster.assertUpload, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${userData.accessToken}`,
                    // Don't set Content-Type for FormData - browser handles it
                },
                body: data,
            });

            // Handle 401 manually for file upload
            if (response.status === 401) {
                const errorData = await response.json();
                if (
                    errorData.status === 'FAIL' &&
                    errorData.statusCode === 401 &&
                    errorData.error === 'Session expired or invalid'
                ) {
                    // Import tokenRefresh from apiInterceptor
                    const { tokenRefresh } = await import("../utils/apiInterceptor");
                    const newToken = await tokenRefresh();

                    // Retry with new token
                    const retryResponse = await fetch(ServiceProvider.docMaster.assertUpload, {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${newToken}`,
                        },
                        body: data,
                    });

                    if (!retryResponse.ok) {
                        const retryError = await retryResponse.json();
                        throw new Error(retryError.message || "Upload failed");
                    }

                    return await retryResponse.json();
                }
            }

            if (!response.ok) {
                console.warn("⚠️ Upload failed with status:", response.status);
                const errorData = await response.json();
                console.error("❌ Server error response:", errorData);
                throw new Error(errorData.message || "Upload failed");
            }

            return await response.json();
        } catch (error) {
            console.error("❌ Upload error:", error);
            throw new Error(error.message || "Unable to upload asset");
        }
    }

    static async removeAsset(removeData) {
        try {
            const response = await apiCall(ServiceProvider.docMaster.remove, {
                method: 'POST',
                body: JSON.stringify(removeData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to remove asset");
            }

            return await response.json();
        } catch (error) {
            console.error("Error removing asset:", error);
            throw new Error(error.message || "Unable to remove asset");
        }
    }

    static async searchByTpl(tplNo) {
        try {
            const response = await apiCall(`${ServiceProvider.docMaster.searchText}${tplNo}`, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Search failed");
            }

            return await response.json();
        } catch (error) {
            console.error("Error searching by TPL:", error);
            throw new Error(error.message || "Unable to search by TPL No");
        }
    }

    static async searchByPart(partNo) {
        try {
            const response = await apiCall(`${ServiceProvider.docMaster.searchByPart}${partNo}`, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Search failed");
            }

            return await response.json();
        } catch (error) {
            console.error("Error searching by Part:", error);
            throw new Error(error.message || "Unable to search by Part No");
        }
    }
}

export default DocMasterService;